/**
 * ============================================================================
 * FILE: api.js
 * PATH: services/shared/api.js
 * LINES: 836
 * ============================================================================
 * 
 * PURPOSE:
 * Centralized API service layer. Provides axios instance with interceptors and
 * all API service objects (networkAPI, optimizationAPI, dataAPI, etc.).
 * Handles workspace_id injection, error handling, and demo mode static data.
 * 
 * ARCHITECTURE:
 *   Component/Composable → API Service → Axios → Backend
 * 
 * DOMAIN: Shared Services
 * LAYER: Service (Data Layer)
 * 
 * ============================================================================
 * MAIN EXPORTS & LINE NUMBERS:
 * ============================================================================
 * 
 * DEFAULT EXPORT:
 * - api (axios instance)                  Lines 11-17
 *   Configured axios instance with interceptors
 * 
 * API SERVICE OBJECTS:
 * - networkAPI                             Lines 70-220
 *   Methods:
 *     - getNetworkData()                   Lines 84-111
 *     - getScenarioCosts()                 Lines 116-211
 *     - getScenarios()                     Lines 216-219
 * 
 * - optimizationAPI                        Lines 226-280
 *   Methods:
 *     - getOptimizationConfig()            Lines 230-238
 *     - saveOptimizationConfig()           Lines 243-246
 *     - runOptimization()                  Lines 253-268
 *     - getResults()                       Lines 274-279
 * 
 * - configAPI                              Lines 286-326
 *   Methods:
 *     - getCostModelingConfig()            Lines 290-293
 *     - saveCostModelingConfig()           Lines 298-301
 *     - getServiceLevelConfig()             Lines 306-309
 *     - saveServiceLevelConfig()           Lines 314-317
 *     - calculateShippingCostImpact()      Lines 322-325
 * 
 * - dataAPI                                Lines 332-598
 *   Methods:
 *     - getFacilities()                    Lines 336-339
 *     - saveFacilities()                    Lines 344-347
 *     - getLanes()                          Lines 352-355
 *     - saveLanes()                         Lines 360-363
 *     - getSources()                        Lines 368-371
 *     - saveSources()                       Lines 376-379
 *     - getDemand()                         Lines 384-387
 *     - getBrownfieldLanes()                Lines 392-395
 *     - getBrownfieldFacilities()           Lines 400-403
 *     - importData()                        Lines 408-482
 *     - clearScenarioData()                 Lines 487-498
 *     - getSupplyChainPaths()               Lines 503-506
 *     - getUnitOfMeasure()                 Lines 511-514
 *     - getReferenceAttributes()           Lines 519-522
 *     - getServiceLevel()                   Lines 527-530
 *     - getGreenfieldMatching()             Lines 535-538
 *     - getGreenfieldTables()              Lines 543-546
 *     - saveGreenfieldMatching()           Lines 551-554
 *     - exportData()                        Lines 559-587
 *     - downloadTemplate()                 Lines 592-597
 * 
 * - mapCustomAPI                          Lines 603-648
 *   Methods:
 *     - getIconSettings()                  Lines 607-610
 *     - saveIconSettings()                  Lines 615-618
 *     - resetIconSettings()                Lines 623-626
 *     - getLaneSettings()                   Lines 631-638
 *     - saveLaneSettings()                  Lines 639-647
 * 
 * - reportingAPI                           Lines 649-803
 *   Methods:
 *     - exportPPT()                         Lines 664-680
 *     - getExecutiveReport()                Lines 681-688
 *     - getCustomersReport()                Lines 689-696
 *     - getShippingLaneReport()             Lines 697-704
 *     - getFacilitiesReport()              Lines 705-712
 *     - downloadFacilitiesExcelWithCharts() Lines 713-726
 *     - downloadScenarioComparisonExcelWithCharts() Lines 727-740
 *     - downloadPurchaseMethodChangesExcel() Lines 741-754
 *     - downloadCustomersExcelWithCharts() Lines 755-769
 *     - downloadLanesExcelWithCharts()      Lines 770-784
 *     - downloadExecutiveExcelWithCharts() Lines 785-803
 * 
 * - presentationSettingsAPI               Lines 804-836
 *   Methods:
 *     - getSettings()                       Lines 808-817
 *     - saveSettings()                      Lines 818-828
 *     - deleteSettings()                    Lines 829-836
 * 
 * ============================================================================
 * INTERCEPTORS:
 * ============================================================================
 * 
 * Request Interceptor (Lines 20-53):
 * - Auto-adds workspace_id to all requests
 * - Gets workspace_id from URL param or store
 * - Adds to params (GET) or body (POST/PUT/PATCH)
 * 
 * Response Interceptor (Lines 56-64):
 * - Global error handling
 * - Logs errors to console
 * 
 * ============================================================================
 * DEPENDENCIES:
 * ============================================================================
 * 
 * External:
 * - axios
 * - @/stores/app (useAppStore)
 * - @/data/staticDemoData (STATIC_DEMO_COSTS, etc.)
 * 
 * ============================================================================
 * USAGE EXAMPLES:
 * ============================================================================
 * 
 * // Import default axios instance
 * import api from '@/core/api'
 * const response = await api.get('/endpoint')
 * 
 * // Import specific API service
 * import { dataAPI } from '@/core/api'
 * const facilities = await dataAPI.getFacilities()
 * 
 * // Import multiple services
 * import { optimizationAPI, dataAPI } from '@/core/api'
 * await optimizationAPI.runOptimization({ scenario_id: 1 })
 * 
 * ============================================================================
 * DEMO MODE:
 * ============================================================================
 * 
 * For workspace_id=0 (demo), some methods return static demo data instead
 * of making API calls. This provides instant loading for demo users.
 * 
 * ============================================================================
 */

import axios from 'axios'
import { useAppStore } from '@/core/store'

// Create axios instance with default config
export const api = axios.create({
  baseURL: '/api', // Relative URL for App Engine deployment
  timeout: 300000, // 5 minutes for optimization
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor — attaches the session token and the workspace.
//
// The token is what actually determines which tenant the backend serves. This
// client previously sent no Authorization header at all (the line here read
// "Add any auth tokens here in the future"), so every data request was
// anonymous and the backend fell back to trusting the workspace_id below. That
// made a URL like ?workspace_id=15 sufficient to read another tenant's data.
//
// workspace_id is still sent, but the backend now treats it as a claim to
// verify against the session rather than as an instruction: a value that does
// not match the signed-in user's own workspace is rejected with 403, and any
// non-demo value without a session is rejected with 401.
api.interceptors.request.use(
  (config) => {
    const appStore = useAppStore()

    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers = config.headers || {}
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    // Get workspace_id from URL parameter first, then from store
    const urlParams = new URLSearchParams(window.location.search)
    const workspaceIdFromUrl = urlParams.get('workspace_id')
    const effectiveWorkspaceId = workspaceIdFromUrl ? parseInt(workspaceIdFromUrl) : (appStore.workspaceId || 0)

    // Add workspace_id based on request method
    if (config.method?.toLowerCase() === 'get') {
      // For GET requests, add to params
      config.params = config.params || {}
      if (config.params.workspace_id === undefined || config.params.workspace_id === null) {
        config.params.workspace_id = effectiveWorkspaceId
      }
    } else if (['post', 'put', 'patch'].includes(config.method?.toLowerCase())) {
      // For POST/PUT/PATCH, add to body
      config.data = config.data || {}
      if (config.data.workspace_id === undefined || config.data.workspace_id === null) {
        config.data.workspace_id = effectiveWorkspaceId
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle errors globally
    const message = error.response?.data?.error || error.message || 'An error occurred'
    console.error('API Error:', message)
    return Promise.reject(error)
  }
)

// ============================================================================
// NETWORK VISUALIZATION API
// ============================================================================

export const networkAPI = {
  /**
   * Get network data for visualization
   * 
   * @param {string} scenario - Scenario name ('Current Baseline' or scenario name)
   * @param {number|null} scenarioId - Scenario ID for optimized scenarios
   * @param {boolean} bustCache - If true, adds timestamp to URL to bypass caches
   * 
   * Cache Busting:
   * - When bustCache=true, adds ?_t=timestamp to URL
   * - This forces browser/proxy to treat it as a new request
   * - Use after optimization completes to guarantee fresh data
   * - Example: /network/data?mode=optimized&scenario_id=2&_t=1634567890123
   */
  async getNetworkData(scenario = 'Current Baseline', scenarioId = null, bustCache = false, workspaceId = null) {
    const mode = scenario === 'Current Baseline' ? 'baseline' : 'optimized'

    const appStore = useAppStore()
    const effectiveWorkspaceId = workspaceId !== null ? workspaceId : appStore.workspaceId

    // Use params object to avoid duplicate workspace_id from interceptor
    const params = {
      mode,
      workspace_id: effectiveWorkspaceId
    }

    if (scenarioId) {
      params.scenario_id = scenarioId
    }

    if (bustCache) {
      params._t = Date.now()
    }

    const response = await api.get('/network/data', { params })
    return response.data
  },

  /**
   * Get aggregated costs for a scenario
   */
  async getScenarioCosts(scenario = 'Current Baseline', scenarioId = null, workspaceId = null) {
    // Check if in demo/enterprise mode - use static data
    const appStore = useAppStore()
    
    // ✅ Get effective workspace_id from URL or params
    const urlParams = new URLSearchParams(window.location.search)
    const workspaceIdFromUrl = urlParams.get('workspace_id')
    const effectiveWorkspaceId = workspaceIdFromUrl ? parseInt(workspaceIdFromUrl) : (workspaceId !== null ? workspaceId : appStore.workspaceId)
    const isTestWorkspace = effectiveWorkspaceId !== 0
    
    // Only use static data for workspace 0 (demo website)
    // Test workspaces (workspace_id != 0) ALWAYS use API data
    if ((appStore.subscriptionTier === 'demo' || appStore.subscriptionTier === 'enterprise') && !isTestWorkspace) {
      // Determine which scenario (1 = baseline, 2 = optimized)
      const isBaseline = scenario === 'Current Baseline' || scenario === 'Baseline' || scenarioId === 1
      
      if (isBaseline) {
        // Scenario 1: Current Baseline
        return {
          costs: {
            total_cost: STATIC_DEMO_BASELINE_COSTS.total_cost,
            fixed_cost: STATIC_DEMO_BASELINE_COSTS.fixed_cost,
            variable_cost: STATIC_DEMO_BASELINE_COSTS.variable_cost,
            shipping_cost: STATIC_DEMO_BASELINE_COSTS.shipping_cost
          },
          demand_stats: {
            total_units: STATIC_DEMO_BASELINE_DEMAND_STATS.total_units,
            fulfilled_percentage: STATIC_DEMO_BASELINE_DEMAND_STATS.fulfilled_percentage,
            product_count: STATIC_DEMO_BASELINE_DEMAND_STATS.product_count
          }
        }
      } else {
        // Scenario 2: Optimized Baseline
        return {
          costs: {
            total_cost: STATIC_DEMO_COSTS.total_cost,
            fixed_cost: STATIC_DEMO_COSTS.fixed_cost,
            variable_cost: STATIC_DEMO_COSTS.variable_cost,
            shipping_cost: STATIC_DEMO_COSTS.shipping_cost
          },
          demand_stats: {
            total_units: STATIC_DEMO_DEMAND_STATS.total_units,
            fulfilled_percentage: STATIC_DEMO_DEMAND_STATS.fulfilled_percentage,
            product_count: STATIC_DEMO_DEMAND_STATS.product_count
          }
        }
      }
    }
    
    // For other tiers, load from API with workspace_id
    // ✅ CRITICAL FIX: If scenarioId provided, ALWAYS use /scenarios/{id}/results
    if (scenarioId) {
      const response = await api.get(`/scenarios/${scenarioId}/results`)
      const data = response.data || {}
      
      return {
        costs: {
          total_cost: data.total_cost || 0,
          fixed_cost: data.fixed_cost || 0,
          variable_cost: data.variable_cost || 0,
          shipping_cost: data.transport_cost || 0,
          holding_cost: data.holding_cost || 0
        },
        demand_stats: {
          total_units: data.total_demand || 0,
          fulfilled_percentage: data.demand_fulfilled || 0,
          product_count: data.product_count || 0
        }
      }
    }
    
    // Fallback: no scenarioId provided
    return {
      costs: {
        total_cost: 0,
        fixed_cost: 0,
        variable_cost: 0,
        shipping_cost: 0,
        holding_cost: 0
      },
      demand_stats: {
        total_units: 0,
        fulfilled_percentage: 0,
        product_count: 0
      }
    }
  },

  /**
   * Get list of available scenarios
   */
  async getScenarios() {
    const response = await api.get('/network/status')
    return response.data.scenarios || ['Current Baseline']
  }
}

// ============================================================================
// OPTIMIZATION API
// ============================================================================

export const optimizationAPI = {
  /**
   * Get optimization configuration for a scenario
   */
  async getOptimizationConfig(scenarioId, workspaceId) {
    const response = await api.get('/optimization/config/get', {
      params: {
        scenario_id: scenarioId,
        workspace_id: workspaceId
      }
    })
    return response.data
  },

  /**
   * Suggested uncertainty settings for a scenario, each with the reason it is
   * suggested. Deliberately separate from getOptimizationConfig: that returns
   * what the user CHOSE, this returns what their data IMPLIES. Merging the two
   * would make a recommendation indistinguishable from a decision the moment
   * the config was saved.
   *
   * Never throws. Losing a recommendation costs the user an explanation, not
   * the ability to configure the run, so it must not take the config screen
   * down with it.
   */
  /**
   * Choose a footprint across scenarios, and price the deterministic answer
   * beside it. Synchronous — the strategic model is small by construction and
   * refuses anything that would not fit before building a single variable.
   *
   * Errors are returned, not thrown: every refusal the stochastic module makes
   * (model too large, no candidate sites, no scenario history) arrives as a 400
   * with a message written to be read by the person who triggered it.
   */
  async runDesignUnderUncertainty(scenarioId) {
    try {
      const response = await api.post('/optimization/design-under-uncertainty', {
        scenario_id: scenarioId
      })
      return response.data
    } catch (err) {
      return {
        status: 'error',
        message: err?.response?.data?.message || err?.message || 'Design run failed'
      }
    }
  },

  async getUncertaintyRecommendations(scenarioId) {
    try {
      const response = await api.get('/optimization/uncertainty/recommendations', {
        params: { scenario_id: scenarioId }
      })
      return response.data?.recommendations || {}
    } catch (err) {
      console.warn('[uncertainty] recommendations unavailable:', err?.message)
      return {}
    }
  },
  
  /**
   * Save optimization configuration and trigger preprocessing
   */
  async saveOptimizationConfig(config) {
    const response = await api.post('/optimization/config/save', config)
    return response.data
  },
  
  /**
   * Run optimization
   * ✅ CRITICAL: Use scenario_id from params (no hardcoded default)
   * Each scenario is isolated - optimization writes to the scenario_id specified
   */
  async runOptimization(params = {}) {
    // Pass through ALL params to backend (including workspace_id)
    // ✅ SCENARIO ISOLATION: Use scenario_id from params, don't default to 2
    // If scenario_id is not provided, let the backend handle it (it will use 1 as default)
    const response = await api.post('/optimization/run', {
      scenario_id: params.scenario_id,  // ✅ FIX: No default - use what's provided
      use_baseline: params.use_baseline !== false,
      workspace_id: params.workspace_id || 0,
      // Pass through optimization config
      objective: params.objective,
      facilityConfig: params.facilityConfig,
      fixedN: params.fixedN,
      multipliers: params.multipliers,
      additionalOptions: params.additionalOptions
    })
    return response.data
  },

  /**
   * Get optimization results
   */
  async getResults(scenarioId = 2) {
    const response = await api.get('/optimization/results', {
      params: { scenario_id: scenarioId }
    })
    return response.data
  }
}

// ============================================================================
// CONFIGURATION API
// ============================================================================

export const configAPI = {
  /**
   * Get cost modeling configuration
   */
  async getCostModelingConfig() {
    const response = await api.get('/parameters/cost-modeling-config')
    return response.data
  },

  /**
   * Get service level configuration
   */
  async getServiceLevelConfig() {
    const response = await api.get('/parameters/service-level-config')
    return response.data
  },

  /**
   * Save service level configuration
   */
  async saveServiceLevelConfig(config) {
    const response = await api.post('/parameters/save-service-level', config)
    return response.data
  },

}

// ============================================================================
// DATA MANAGEMENT API
// ============================================================================

export const dataAPI = {
  /**
   * Get facilities data
   */
  async getFacilities() {
    const response = await api.get('/data/facilities')
    return response.data
  },

  /**
   * Save facilities data
   */
  async saveFacilities(facilities) {
    const response = await api.post('/data/save-facilities', { facilities })
    return response.data
  },

  /**
   * Get lanes data for the current scenario
   */
  async getLanes(scenarioId = null) {
    const params = {}
    if (scenarioId) params.scenario_id = scenarioId
    const response = await api.get('/data/lanes', { params })
    return response.data
  },

  /**
   * Save lanes data
   */
  async saveLanes(lanes) {
    const response = await api.post('/data/save-lanes', { lanes })
    return response.data
  },

  /**
   * Get sources data
   */
  async getSources() {
    const response = await api.get('/data/sources')
    return response.data
  },

  /**
   * Save sources data
   */
  async saveSources(sources) {
    const response = await api.post('/data/save-sources', { sources })
    return response.data
  },

  /**
   * Get demand data
   */
  async getDemand() {
    const response = await api.get('/data/demand')
    return response.data
  },

  /**
   * Get brownfield lanes (for baseline scenario)
   */
  async getBrownfieldLanes() {
    // TODO: Add endpoint or use getLanes() with scenario parameter
    return this.getLanes()
  },

  /**
   * Get brownfield facilities (for baseline scenario)
   */
  async getBrownfieldFacilities() {
    // TODO: Add endpoint or use getFacilities() with scenario parameter
    return this.getFacilities()
  },

  /**
   * Import Excel data
   */
  async importData(file, dataType, options = {}) {
    const formData = new FormData()
    formData.append('file', file)

    // Ensure workspace_id travels with multipart requests (interceptor can't append to FormData)
    try {
      const appStore = useAppStore()
      const effectiveWorkspaceId = appStore.workspaceId || 0
      formData.append('workspace_id', effectiveWorkspaceId)
    } catch (err) {
      console.warn('⚠️ [importData] Could not append workspace_id to FormData:', err)
    }

    // ✅ SCENARIO ISOLATION: Handle scenario filter for imports
    // This determines whether to import to current scenario or all scenarios
    let scenarioFilter = options.scenario_filter || 'current_scenario'
    formData.append('scenario_filter', scenarioFilter)
    console.log('📋 [importData] Scenario filter:', scenarioFilter)

    let endpoint = ''
    switch (dataType) {
      case 'current_baseline':
        formData.append('data_type', 'current_baseline')
        endpoint = '/data/import-input-baseline'
        // ✅ SCENARIO ISOLATION: Send scenario_id for baseline imports (only if not "all")
        // If scenario_filter is "all", backend will handle importing to all scenarios
        if (scenarioFilter !== 'all') {
          try {
            const appStore = useAppStore()
            const scenarioId = appStore.currentScenarioId || 1
            formData.append('scenario_id', scenarioId)
            console.log('📋 [importData] Baseline import - scenario_id:', scenarioId)
          } catch (err) {
            console.warn('⚠️ [importData] Could not append scenario_id to FormData:', err)
            // Default to scenario 1 if appStore not available
            formData.append('scenario_id', 1)
          }
        } else {
          console.log('📋 [importData] Baseline import - importing to ALL scenarios')
        }
        break
      case 'configuration':
        formData.append('data_type', 'configuration')
        endpoint = '/data/import-input-configuration'
        // ✅ SCENARIO ISOLATION: Send scenario_id for configuration imports
        try {
          const appStore = useAppStore()
          const scenarioId = appStore.currentScenarioId || 1
          formData.append('scenario_id', scenarioId)
          console.log('📋 [importData] Configuration import - scenario_id:', scenarioId)
        } catch (err) {
          console.warn('⚠️ [importData] Could not append scenario_id to FormData:', err)
          // Default to scenario 1 if appStore not available
          formData.append('scenario_id', 1)
        }
        break
      case 'optimized_inputs':
        endpoint = '/data/import-scenario-input'
        formData.append('target', 'optimized')
        if (Array.isArray(options.scenarioIds) && options.scenarioIds.length > 0) {
          formData.append('scenario_ids', options.scenarioIds.join(','))
        }
        break
      case 'scenario_inputs':
        endpoint = '/data/import-scenario-input'
        formData.append('target', 'scenario')
        if (!options.scenarioId) {
          throw new Error('scenarioId is required for scenario input imports')
        }
        formData.append('scenario_id', options.scenarioId)
        if (options.scenarioName) {
          formData.append('scenario_name', options.scenarioName)
        }
        break
      default:
        throw new Error(`Unsupported data type: ${dataType}`)
    }

    const response = await api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 900000  // 15 minutes for large file imports (2K products)
    })
    return response.data
  },

  /**
   * Clear scenario input data
   */
  async clearScenarioData(scenarioId, scenarioName) {
    const payload = {
      scenario_label: scenarioName
      // workspace_id is automatically added by interceptor
    }
    // Only include scenario_id if it's not null (null means delete all scenarios)
    if (scenarioId !== null && scenarioId !== undefined) {
      payload.scenario_id = scenarioId
    }
    const response = await api.post('/data/clear-scenario-data', payload)
    return response.data
  },

  /**
   * Get supply chain paths (configured or inferred from network data)
   */
  async getSupplyChainPaths(scenarioId = null) {
    const params = {}
    if (scenarioId) params.scenario_id = scenarioId
    const response = await api.get('/data/supply-chain-paths', { params })
    // ✅ FIX: Backend returns { status, paths } - extract the paths array
    const data = response.data
    if (data && Array.isArray(data.paths)) {
      return data.paths
    }
    if (Array.isArray(data)) {
      return data
    }
    return []
  },

  /**
   * Get unit of measure settings
   */
  async getUnitOfMeasure() {
    const response = await api.get('/data/unit-of-measure')
    return response.data
  },

  /**
   * Get unit conversions for a workspace/scenario
   */
  async getUnitConversions(scenarioId = null) {
    const params = scenarioId ? { scenario_id: scenarioId } : {}
    const response = await api.get('/data/unit-conversions', { params })
    return response.data
  },

  /**
   * Convert a value from one unit to another
   */
  async convertUnit(value, fromUnit, toUnit, scenarioId = null) {
    const response = await api.post('/data/convert-unit', {
      value,
      from_unit: fromUnit,
      to_unit: toUnit,
      scenario_id: scenarioId
    })
    return response.data
  },

  /**
   * Get reference attributes (location types, etc)
   */
  async getReferenceAttributes() {
    const response = await api.get('/data/reference-attributes')
    return response.data
  },

  /**
   * Get service level configuration
   */
  async getServiceLevel() {
    const response = await api.get('/data/service-level')
    return response.data
  },

  /**
   * Get greenfield matching configuration
   */
  async getGreenfieldMatching() {
    const response = await api.get('/data/greenfield-matching')
    return response.data
  },

  /**
   * Get greenfield table names
   */
  async getGreenfieldTables() {
    const response = await api.get('/data/greenfield-tables')
    return response.data
  },

  /**
   * Save greenfield matching configuration
   */
  async saveGreenfieldMatching(mappings) {
    const response = await api.post('/data/greenfield-matching', { mappings })
    return response.data
  },

  /**
   * Export data to Excel
   */
  async exportData(dataType, options = {}) {
    const payload = { data_type: dataType }

    // Add scenario filter if provided
    if (options.scenario_filter) {
      payload.scenario_filter = options.scenario_filter
      console.log('📋 [exportData] Scenario filter:', options.scenario_filter)
    }

    if (dataType === 'scenario_inputs') {
      if (!options.scenarioId) {
        throw new Error('scenarioId is required to export scenario inputs')
      }
      payload.scenario_id = options.scenarioId
      if (options.scenarioName) {
        payload.scenario_name = options.scenarioName
      }
    }

    if (dataType === 'optimized_inputs' && Array.isArray(options.scenarioIds) && options.scenarioIds.length > 0) {
      payload.scenario_ids = options.scenarioIds
    }

    const response = await api.post('/data/export', 
      payload,
      { responseType: 'blob' }
    )
    return response
  },

  /**
   * Download Excel template
   */
  async downloadTemplate() {
    const response = await api.get('/data/download-template', {
      responseType: 'blob'
    })
    return response
  }
}

/**
 * Map Customization API
 * ✅ SCENARIO-SPECIFIC: All methods now support scenario_id and workspace_id parameters
 */
export const mapCustomAPI = {
  /**
   * Get icon settings for a specific scenario
   * @param {number} scenarioId - Scenario ID
   * @param {number} workspaceId - Workspace ID (defaults to 0)
   */
  async getIconSettings(scenarioId, workspaceId = 0) {
    const response = await api.get(`/map/icons/${scenarioId}`, {
      params: { workspace_id: workspaceId }
    })
    return response.data
  },

  /**
   * Get lane settings for a specific scenario
   * @param {number} scenarioId - Scenario ID
   * @param {number} workspaceId - Workspace ID (defaults to 0)
   */
  async getLaneSettings(scenarioId, workspaceId = 0) {
    const response = await api.get(`/map/lanes/${scenarioId}`, {
      params: { workspace_id: workspaceId }
    })
    return response.data
  },

}

// ============================================================================
// REPORTING API
// ============================================================================

export const reportingAPI = {

  /**
   * Export PPT with McKinsey-style presentation
   */
  async exportPPT(options = {}) {
    const params = new URLSearchParams()
    if (options.scenarios && options.scenarios.length > 0) {
      params.append('scenarios', JSON.stringify(options.scenarios))
    } else {
      params.append('scenarios', '[]')  // Empty = all scenarios
    }
    
    const response = await api.get(`/reports/export-ppt?${params.toString()}`, {
      responseType: 'blob'
    })
    return response
  },

  /**
   * Get executive report data
   */
  async getExecutiveReport() {
    const response = await api.get('/reports/executive-report')
    return response.data
  },

  /**
   * Get customers report data
   */
  async getCustomersReport() {
    const response = await api.get('/reports/customers-report')
    return response.data
  },

  /**
   * Get shipping lane report data
   */
  async getShippingLaneReport() {
    const response = await api.get('/reports/shipping-lane-report')
    return response.data
  },

  /**
   * Get facilities report data
   */
  async getFacilitiesReport() {
    const response = await api.get('/reports/facilities-report')
    return response.data
  },

  /**
   * Download facilities Excel report with embedded charts (from Python backend)
   */
  async downloadFacilitiesExcelWithCharts(baselineScenarioId = 1, currentScenarioId = null) {
    const params = {
      baseline_scenario: baselineScenarioId
    }
    if (currentScenarioId) {
      params.current_scenario = currentScenarioId
    }
    const response = await api.get('/reports/facilities-excel-with-charts', {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  async downloadScenarioComparisonExcelWithCharts(baselineScenarioId = 1, currentScenarioId = null) {
    const params = {
      baseline_scenario: baselineScenarioId
    }
    if (currentScenarioId) {
      params.current_scenario = currentScenarioId
    }
    const response = await api.get('/reports/scenario-comparison-excel-with-charts', {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  async downloadPurchaseMethodChangesExcel(baselineScenarioId = 1, optimizedScenarioId = null) {
    const params = {
      baseline_scenario: baselineScenarioId
    }
    if (optimizedScenarioId) {
      params.optimized_scenario = optimizedScenarioId
    }
    const response = await api.get('/reports/purchase-method-changes-excel', {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  async downloadCustomersExcelWithCharts(baselineScenarioId = 1, currentScenarioId = null) {
    const params = {
      baseline_scenario: baselineScenarioId,
      t: Date.now()  // Cache busting
    }
    if (currentScenarioId) {
      params.current_scenario = currentScenarioId
    }
    const response = await api.get('/reports/customers-excel-with-charts', {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  async downloadLanesExcelWithCharts(baselineScenarioId = 1, currentScenarioId = null) {
    const params = {
      baseline_scenario: baselineScenarioId,
      t: Date.now()  // Cache busting
    }
    if (currentScenarioId) {
      params.current_scenario = currentScenarioId
    }
    const response = await api.get('/reports/lanes-excel-with-charts', {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  async downloadExecutiveExcelWithCharts(options = {}) {
    // Add timestamp to bust cache
    const params = {
      t: Date.now(),
      scenarios: options.scenarios ? JSON.stringify(options.scenarios) : '[]'
    }
    
    const response = await api.get(`/reports/executive-excel-with-charts`, {
      params,
      responseType: 'blob'
    })
    return response.data
  }
}

// ============================================================================
// PRESENTATION SETTINGS API
// ============================================================================

export const presentationSettingsAPI = {
  /**
   * Get presentation settings for a specific scenario
   */
  async getSettings(scenarioId, workspaceId = 0) {
    const response = await api.get(`/presentation-settings/${scenarioId}`, {
      params: { workspace_id: workspaceId }
    })
    return response.data
  },

  /**
   * Save presentation settings for a specific scenario
   */
  async saveSettings(scenarioId, settings, workspaceId = 0) {
    const response = await api.post(`/presentation-settings/${scenarioId}`, {
      workspace_id: workspaceId,
      settings
    })
    return response.data
  },

  /**
   * Delete/reset presentation settings for a specific scenario
   */
  async deleteSettings(scenarioId, workspaceId = 0) {
    const response = await api.delete(`/presentation-settings/${scenarioId}`, {
      params: { workspace_id: workspaceId }
    })
    return response.data
  }
}

export default api